import { Listbox, ListboxOption } from '@headlessui/react'

import { useEffect, useState } from 'react'

import type { Option, OptionGroup, Value } from './types'
import { SelectListboxShell } from './SelectListboxShell'
import { optionClassName } from './optionClasses'

type GroupedMenuSelectProps<T extends Value> = {
  onChange: (option: Option<T>) => void
  defaultValue: Option<T>
  groups: OptionGroup<T>[]
}

/**
 * A select dropdown that shows options in groups with non-selectable section headers.
 * Headers are visual only and are skipped for keyboard navigation (only options are focusable).
 */
export default function GroupedMenuSelect<T extends Value>({
  onChange,
  defaultValue,
  groups,
}: GroupedMenuSelectProps<T>) {
  const flatOptions = groups.flatMap((g) => g.options)
  const [selected, setSelected] = useState<Option<T>>(
    flatOptions.find((opt) => opt.value === defaultValue.value) ?? defaultValue
  )

  // Update selected value from outside if defaultValue changes (as in the case of a language change)
  useEffect(() => {
    const next = flatOptions.find((opt) => opt.value === defaultValue.value) ?? defaultValue
    setSelected(next)
  }, [defaultValue, groups])

  const handleChange = (option: Option<T> | null) => {
    if (option) {
      setSelected(option)
      onChange(option)
    }
  }

  return (
    <Listbox value={selected} onChange={handleChange}>
      <SelectListboxShell selected={selected}>
        {groups.map((group) => (
          <div key={group.header} role="group" aria-label={group.header}>
            <div
              className="px-2 py-1.5 text-sm font-semibold tracking-wide text-neutral-midfi-grey"
              role="presentation"
            >
              {group.header}
            </div>
            {group.options.map((option) => {
              const isSelected = option.value === selected.value
              return (
                <ListboxOption
                  key={option.value}
                  value={option}
                  disabled={isSelected}
                  className={optionClassName(isSelected, true)}
                >
                  {option.label}
                </ListboxOption>
              )
            })}
          </div>
        ))}
      </SelectListboxShell>
    </Listbox>
  )
}
